import { useState, useEffect, useCallback } from 'react';
import { apiService, type WorkflowStatus } from '../services/api';

export function useWorkflows(workflowId?: string) {
    const [workflows, setWorkflows] = useState<WorkflowStatus[]>([]);
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchWorkflows = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const workflowsData = await apiService.getWorkflows();
            setWorkflows(workflowsData);

            // Auto-select workflow if ID is provided
            if (workflowId) {
                const workflow = workflowsData.find(w => w.id === workflowId);
                setSelectedWorkflow(workflow || null);
            } else if (workflowsData.length > 0 && !selectedWorkflow) {
                // Auto-select first workflow if none is selected
                setSelectedWorkflow(workflowsData[0]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch workflows');
        } finally {
            setLoading(false);
        }
    }, [workflowId, selectedWorkflow]);

    const fetchWorkflowStatus = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            const workflow = await apiService.getWorkflowStatus(id);
            setSelectedWorkflow(workflow);

            // Update the workflow in the list if it exists
            setWorkflows(prev =>
                prev.map(w => w.id === id ? workflow : w)
            );
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch workflow status');
        } finally {
            setLoading(false);
        }
    }, []);

    const cancelWorkflow = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            await apiService.cancelWorkflow(id);

            // Update the workflow status in the list
            setWorkflows(prev =>
                prev.map(w => w.id === id ? { ...w, status: 'cancelled' as const } : w)
            );

            if (selectedWorkflow?.id === id) {
                setSelectedWorkflow(prev => prev ? { ...prev, status: 'cancelled' as const } : null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to cancel workflow');
        } finally {
            setLoading(false);
        }
    }, [selectedWorkflow]);

    // Initial fetch
    useEffect(() => {
        fetchWorkflows();
    }, [fetchWorkflows]);

    // Auto-refresh running workflows
    useEffect(() => {
        const runningWorkflows = workflows.filter(w =>
            w.status === 'running' || w.status === 'pending'
        );

        if (runningWorkflows.length === 0) {
            return;
        }

        const interval = setInterval(() => {
            fetchWorkflows();
        }, 5000);

        return () => clearInterval(interval);
    }, [workflows, fetchWorkflows]);

    return {
        workflows,
        selectedWorkflow,
        loading,
        error,
        fetchWorkflows,
        fetchWorkflowStatus,
        cancelWorkflow,
        setSelectedWorkflow
    };
}